/**
 * Keeping a visit where it already is.
 *
 *   "I can't save these changes I can't figure out why"
 *   "It won't let me add the NAD"
 *   "It's the NAD it won't book it for some reason both Peter and Bruce are
 *    going over to blvd"
 *
 * Adding a 3-hour NAD to a visit at lunchtime offers no times at all - it
 * cannot finish before the last shift ends - and Save sat greyed out on a
 * visit that already HAD a time. Two clients went to Boulevard over it.
 *
 * Adding a treatment should lengthen the visit where it stands. Each one
 * keeps the chair and clinician it already had; a newly added one follows
 * whatever came before it.
 */

export interface PlannedSegment {
  service_id: string;
  service_name: string;
  provider_id: string | null;
  room_id: string | null;
  start: string;
  end: string;
  blocked_until: string;
  duration_minutes: number;
  price_cents: number;
}

export interface ExistingLeg {
  service_id: string | null;
  provider_id: string | null;
  room_id: string | null;
  service_name?: string | null;
  price_cents?: number | null;
}

export interface WantedService {
  id: string;
  name: string | null;
  duration_minutes: number | null;
  price_cents: number | null;
}

export interface KeepTimeInput {
  /** When the visit starts now, and stays. */
  startsAt: string;
  /** The treatments she wants on it, in order. */
  wanted: readonly WantedService[];
  /** The legs it has today, so chairs and clinicians survive. */
  current: readonly ExistingLeg[];
  /** The seat she picked at the top of the dialog, if any. */
  seatFilter?: string | null;
  /** Fallbacks for a treatment that was not on the visit before. */
  fallbackProviderId?: string | null;
  fallbackRoomId?: string | null;
}

/** Lay the treatments end to end from the time the visit already has. */
export function keepVisitTime(input: KeepTimeInput): PlannedSegment[] | null {
  const startMs = new Date(input.startsAt).getTime();
  if (!Number.isFinite(startMs) || input.wanted.length === 0) return null;

  let cursor = startMs;
  return input.wanted.map((w, i) => {
    const mins = w.duration_minutes ?? 30;
    // The leg that was already there for this treatment, so its chair and
    // clinician survive; otherwise follow whatever the leg before used.
    const was = input.current.find((leg) => leg.service_id === w.id) ?? input.current[i] ?? null;
    const end = cursor + mins * 60_000;
    const seg: PlannedSegment = {
      service_id: w.id,
      service_name: w.name ?? was?.service_name ?? "Visit",
      provider_id: was?.provider_id ?? input.fallbackProviderId ?? null,
      room_id: input.seatFilter || was?.room_id || input.current[0]?.room_id || input.fallbackRoomId || null,
      start: new Date(cursor).toISOString(),
      end: new Date(end).toISOString(),
      blocked_until: new Date(end).toISOString(),
      duration_minutes: mins,
      price_cents: w.price_cents ?? was?.price_cents ?? 0,
    };
    cursor = end;
    return seg;
  });
}
